import {
  createContext,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { addLogByKey } from 'src/utils/logger';

type PromiseKey = string;

interface UsePromises {
  addPromise: (
    key: PromiseKey,
    promise: Promise<string | undefined>,
    callback?: (result: string | undefined) => void,
  ) => void;
  awaitAllPromises: () => Promise<void>;
  getPendingKeys: () => PromiseKey[];
  getPromiseForKey: (key: PromiseKey) => Promise<string | undefined> | null;
}

const PromisesContext = createContext<UsePromises | null>(null);

export const usePromises = () => {
  const context = useContext(PromisesContext);

  if (!context) {
    throw new Error('Missing PromisesProvider');
  }
  return context;
};

export const PromisesProvider = ({ children }: { children: ReactNode }) => {
  const promisesRef = useRef<Record<PromiseKey, Promise<string | undefined>>>(
    {},
  );
  const [, forceRender] = useState(0); // for stateful feedback if needed

  const addPromise = async (
    key: PromiseKey,
    promise: Promise<string | undefined>,
    callback?: (result: string | undefined) => void,
  ) => {
    promisesRef.current[key] = promise;

    try {
      const result = await promise;

      if (callback) {
        callback(result);
      }
    } catch (error) {
      // Handle errors
      addLogByKey('use-promise-addPromise-catch', {
        key,
        href: window.location.href,
        error,
      });
    } finally {
      delete promisesRef.current[key];
      forceRender((x) => x + 1);
    }
  };

  const awaitAllPromises = async () => {
    await Promise.all(Object.values(promisesRef.current));
  };

  const getPendingKeys = () => Object.keys(promisesRef.current);

  const getPromiseForKey = (key: PromiseKey) =>
    promisesRef.current[key] ?? null;

  return (
    <PromisesContext.Provider
      value={{
        addPromise,
        awaitAllPromises,
        getPendingKeys,
        getPromiseForKey,
      }}
    >
      {children}
    </PromisesContext.Provider>
  );
};
